import { timestampFromDate } from '@bufbuild/protobuf/wkt';
import { Client } from '@connectrpc/connect';

import { Clearable, clearableToWire } from './clearable';
import { AccountService } from './espb/account_service_pb';
import { RegistrationInfoStatus } from './espb/registration_info_status_pb';
import { knownEnumOrDefault, requireFieldNotEmpty } from './grpcUtils';
import { toSyncDto } from './sync';
import {
  Email,
  RegisterUserResponseDto,
  UserId,
  ReferrerId,
  AchievementId,
  SyncUpdate,
  ListAccountsResponseDto,
  AuthenticatorBlockDto,
  ProfileId,
  OrganizationId,
  ProfileDto,
  VaultCreationDto,
  ProfileType,
  RegistrationInfo,
  AuthenticatorCreationDto,
  WebauthnPrfSupportStatus,
  OrganizationType,
} from './types';
import { toPbVaultCreationData } from './vaults';

export default class AccountServiceClient {
  constructor(private readonly espbClient: Client<typeof AccountService>) {}

  async getRegistrationInfo(options: {
    email?: Email;
    profileId?: ProfileId;
  }): Promise<RegistrationInfo> {
    const resp = await this.espbClient.getRegistrationInfo(options);
    return {
      status: knownEnumOrDefault(resp.status, RegistrationInfoStatus),
      organization:
        resp.organization ?
          {
            id: requireFieldNotEmpty(resp.organization.id),
            name: resp.organization.name,
            icon: resp.organization.icon || undefined,
            startCodePrefix: resp.organization.startCodePrefix || undefined,
            profileId: resp.organization.profileId || undefined,
            organizationType: knownEnumOrDefault(
              resp.organization.organizationType,
              OrganizationType,
            ),
          }
        : undefined,
      webauthnAllowed: resp.webauthnAllowed,
      profileType: knownEnumOrDefault(resp.profileType, ProfileType),
    };
  }

  async createWithProfile(
    authenticators: AuthenticatorCreationDto[],
    prefsProfile: ProfileDto,
    metaVault: VaultCreationDto,
    inboxProfile: ProfileDto,
    authenticatorBlock: AuthenticatorBlockDto | undefined,
    referrerId: ReferrerId | undefined,
    preliminaryEmail: Email | undefined,
    sessionUnlockData?: {
      encryptedSecret: Uint8Array;
      unlockMaxExpiresAt?: Date;
    },
    webauthnCredentialData?: {
      requestId: string;
      responseJson: string;
      prfSupportStatus: WebauthnPrfSupportStatus;
    },
    startCode?: string,
    startCodeProfileId?: string,
    browserManagedDeviceToken?: string,
  ): Promise<RegisterUserResponseDto> {
    const resp = await this.espbClient.createWithProfile({
      preliminaryEmail,
      authenticators,
      authenticatorBlock: authenticatorBlock?.blob,
      authenticatorBlockSignature: authenticatorBlock?.signature,
      referrerId,
      preferencesProfile: prefsProfile,
      inboxProfile,
      metaVault: toPbVaultCreationData(metaVault),
      sessionUnlockData:
        sessionUnlockData ?
          {
            encryptedSecret: sessionUnlockData.encryptedSecret,
            unlockMaxExpiresAt:
              sessionUnlockData.unlockMaxExpiresAt ?
                timestampFromDate(sessionUnlockData.unlockMaxExpiresAt)
              : undefined,
          }
        : undefined,
      webauthnCredentialData,
      startCode,
      startCodeProfileId,
      browserManagedDeviceToken,
    });

    const authenticatorDtos = resp.authenticatorIds.flatMap((id, i) => {
      const authenticator = authenticators[i];
      return authenticator ? { ...authenticator.data, id } : [];
    });

    const token = requireFieldNotEmpty(resp.accessToken);
    const loginData = {
      userId: requireFieldNotEmpty(resp.userId),
      sessionId: requireFieldNotEmpty(resp.sessionId),
      token: requireFieldNotEmpty(token.token),
      tokenVersion: resp.tokenVersion,
    };

    return { userId: resp.userId, authenticatorDtos, loginData };
  }

  async createReferrer(referrer: string): Promise<ReferrerId> {
    const resp = await this.espbClient.createReferrer({ referrer });
    return requireFieldNotEmpty(resp.referrerId);
  }

  async addAchievements(achievementIds: Array<AchievementId>): Promise<SyncUpdate> {
    const resp = await this.espbClient.addAchievements({ achievements: achievementIds });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async update({
    preferredLocale,
    userClientSettings,
    profilesLocked,
  }: {
    preferredLocale?: Clearable<string>;
    userClientSettings?: Clearable<string>;
    profilesLocked?: boolean;
  }): Promise<SyncUpdate> {
    const resp = await this.espbClient.update({
      preferredLocale: clearableToWire(preferredLocale),
      userClientSettings: clearableToWire(userClientSettings),
      profilesLocked,
    });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async list(
    knownUsers: {
      readonly id: UserId;
      readonly authenticatorBlockHash?: Uint8Array;
    }[],
    userEmails: Email[],
    profileIds: ProfileId[],
  ): Promise<ListAccountsResponseDto> {
    const resp = await this.espbClient.list({ emails: userEmails, users: knownUsers, profileIds });
    const users = resp.users.map((u) => {
      return {
        ...u,
        id: requireFieldNotEmpty(u.userId),
      };
    });

    return { users };
  }

  async resolveEmails(
    emails: string[],
    failFast?: boolean,
  ): Promise<
    {
      email: Email;
      emailInvalid: boolean;
      userId: UserId | undefined;
      profiles: Array<{
        profileId: ProfileId;
        profileType: ProfileType;
        organizationId: OrganizationId | undefined;
      }>;
    }[]
  > {
    const result = await this.espbClient.resolveEmails({ emails, failFast });
    return result.emails.map((e) => ({
      ...e,
      userId: e.userId || undefined,
      profiles: e.profiles.map((p) => ({
        profileId: requireFieldNotEmpty(p.id),
        profileType: knownEnumOrDefault(p.profileType, ProfileType),
        organizationId: p.organizationId || undefined,
      })),
    }));
  }

  async getPaddleConfig(): Promise<{
    isSandbox: boolean;
    wrapperHost: string;
    vendorId: number;
    productIdMonthly: number;
    productIdYearly: number;
  }> {
    const resp = await this.espbClient.getPaddleConfig({});
    return {
      ...resp,
      vendorId: Number(resp.vendorId),
      productIdMonthly: Number(resp.productIdMonthly),
      productIdYearly: Number(resp.productIdYearly),
    };
  }

  async deleteAccount(): Promise<void> {
    await this.espbClient.delete({});
  }
}
